import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  Plus,
  Receipt,
  Storefront,
  Wallet,
} from '@phosphor-icons/react';
import {
  approvePayout,
  createStore,
  formatMoney,
  listPayouts,
  listStores,
  listTeamFunds,
  type PayoutSummary,
  type StoreSummary,
  type TeamFund,
} from '@/api/shop';
import { listTeams } from '@/api/teams';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { AccessDenied } from '@/components/AccessDenied';
import { LoadError } from '@/components/LoadError';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useConfirm } from '@/components/ConfirmDialog';

// Shop is the lagkassan + webshop admin hub (mobile-app §8). Three tabs mirror
// the three things admins do here in practice: see every team's fund balance at
// a glance, drill into the team stores, and clear pending payouts. Each tab
// uses one API call from frontend/src/api/shop.ts; nothing on this page mutates
// directly (besides the approve-payout click), and even that is org-admin-only
// dual-control gated server-side. Drill-down routes (/shop/teams/:teamID,
// /shop/stores/:storeID) handle ledger entries and product / campaign work.

type ShopTab = 'funds' | 'stores' | 'payouts';

export function Shop() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const [tab, setTab] = useState<ShopTab>('funds');

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('shop.eyebrow')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('shop.title')}
        </h1>
        <p className="text-muted-foreground">{t('shop.subtitle')}</p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as ShopTab)} className="flex flex-col gap-6">
        <TabsList aria-label={t('shop.tabsLabel')}>
          <TabsTrigger value="funds">
            <Wallet className="mr-2 h-4 w-4" weight="bold" /> {t('shop.tabs.funds')}
          </TabsTrigger>
          <TabsTrigger value="stores">
            <Storefront className="mr-2 h-4 w-4" weight="bold" /> {t('shop.tabs.sales')}
          </TabsTrigger>
          <TabsTrigger value="payouts">
            <Receipt className="mr-2 h-4 w-4" weight="bold" /> {t('shop.tabs.payouts')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="funds">
          <FundsTab slug={slug} token={token} />
        </TabsContent>
        <TabsContent value="stores">
          <StoresTab slug={slug} token={token} />
        </TabsContent>
        <TabsContent value="payouts">
          <PayoutsTab slug={slug} token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FUND_SORT_COLUMNS = ['team', 'entries', 'balance'] as const;
type FundSortColumn = (typeof FUND_SORT_COLUMNS)[number];

function FundsTab({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'team-funds', slug],
    queryFn: () => listTeamFunds(token, slug),
    enabled: !!slug,
  });

  const sortState = useTableSortState<FundSortColumn>({
    columns: FUND_SORT_COLUMNS,
    prefix: 'funds',
  });
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'funds',
    defaultSize: 25,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const sorted = useMemo(() => {
    const arr = [...rows];
    const col = sortState.sort ?? 'team';
    const factor = sortState.dir === 'desc' ? -1 : 1;
    arr.sort((a: TeamFund, b: TeamFund) => {
      switch (col) {
        case 'balance':
          return factor * (a.balance_minor - b.balance_minor);
        case 'entries':
          return factor * (a.entries - b.entries);
        case 'team':
        default:
          return factor * a.team_name.localeCompare(b.team_name);
      }
    });
    return arr;
  }, [rows, sortState.sort, sortState.dir]);

  const total = sorted.length;
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * size, page * size),
    [sorted, page, size],
  );

  if (isLoading) {
    return <TableSkeleton columns={5} />;
  }
  if (error) return <ErrorBlock error={error} />;
  if (total === 0) {
    return (
      <EmptyState
        icon={<Wallet weight="duotone" />}
        title={t('shop.funds.emptyTitle')}
        body={t('shop.funds.emptyBody')}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeaderButton
                  column="team"
                  label={t('shop.funds.col.team')}
                  state={sortState}
                />
              </TableHead>
              <TableHead>
                <SortableHeaderButton
                  column="entries"
                  label={t('shop.funds.col.entries')}
                  state={sortState}
                />
              </TableHead>
              <TableHead className="text-right">
                <SortableHeaderButton
                  column="balance"
                  label={t('shop.funds.col.balance')}
                  state={sortState}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((r: TeamFund) => (
              <TableRow
                key={r.team_id}
                onClick={() => navigate(`/orgs/${slug}/shop/teams/${r.team_id}`)}
                className="cursor-pointer"
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-foreground/60" weight="bold" />
                    <span className="font-heading font-semibold truncate">{r.team_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {r.entries > 0
                    ? t('shop.funds.entryCount', { count: r.entries })
                    : t('shop.funds.noEntries')}
                </TableCell>
                <TableCell className="text-right font-heading font-semibold tabular-nums">
                  {formatMoney(r.balance_minor, r.currency || 'SEK')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={total}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
      />
    </div>
  );
}

const SALE_SORT_COLUMNS = ['name', 'team', 'status', 'balance'] as const;
type SaleSortColumn = (typeof SALE_SORT_COLUMNS)[number];

function StoresTab({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'stores', slug],
    queryFn: () => listStores(token, slug),
    enabled: !!slug,
  });

  const sortState = useTableSortState<SaleSortColumn>({
    columns: SALE_SORT_COLUMNS,
    prefix: 'sales',
  });
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'sales',
    defaultSize: 25,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const sorted = useMemo(() => {
    const arr = [...rows];
    const col = sortState.sort ?? 'name';
    const factor = sortState.dir === 'desc' ? -1 : 1;
    arr.sort((a: StoreSummary, b: StoreSummary) => {
      switch (col) {
        case 'balance':
          return factor * (a.balance_minor - b.balance_minor);
        case 'team':
          return factor * a.team_name.localeCompare(b.team_name);
        case 'status':
          return factor * a.status.localeCompare(b.status);
        case 'name':
        default:
          return factor * a.name.localeCompare(b.name);
      }
    });
    return arr;
  }, [rows, sortState.sort, sortState.dir]);

  const total = sorted.length;
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * size, page * size),
    [sorted, page, size],
  );

  if (isLoading) {
    return <TableSkeleton columns={5} />;
  }
  if (error) return <ErrorBlock error={error} />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" weight="bold" /> {t('shop.sales.newSale')}
        </Button>
      </div>
      {createOpen && (
        <CreateStoreInline
          slug={slug}
          token={token}
          onCancel={() => setCreateOpen(false)}
          onCreated={(storeID) => {
            setCreateOpen(false);
            navigate(`/orgs/${slug}/shop/stores/${storeID}`);
          }}
        />
      )}
      {total === 0 ? (
        <EmptyState
          icon={<Storefront weight="duotone" />}
          title={t('shop.sales.emptyTitle')}
          body={t('shop.sales.emptyBody')}
          actions={[
            {
              label: t('shop.sales.newSale'),
              onClick: () => setCreateOpen(true),
              icon: <Plus className="h-4 w-4" weight="bold" />,
            },
          ]}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeaderButton
                      column="name"
                      label={t('shop.sales.col.name')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="team"
                      label={t('shop.sales.col.team')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[8rem]">
                    <SortableHeaderButton
                      column="status"
                      label={t('shop.sales.col.status')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeaderButton
                      column="balance"
                      label={t('shop.sales.col.balance')}
                      state={sortState}
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((s: StoreSummary) => (
                  <TableRow
                    key={s.id}
                    onClick={() => navigate(`/orgs/${slug}/shop/stores/${s.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Storefront className="h-5 w-5 text-foreground/60" weight="bold" />
                        <div className="min-w-0">
                          <div className="font-heading font-semibold truncate">{s.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {t('shop.sales.productCount', { count: s.product_count })}
                            {s.open_orders > 0 &&
                              ` · ${t('shop.sales.openOrders', { count: s.open_orders })}`}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {s.team_name}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">
                      {s.status}
                    </TableCell>
                    <TableCell className="text-right font-heading font-semibold tabular-nums">
                      {formatMoney(s.balance_minor, s.currency || 'SEK')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            total={total}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
        </div>
      )}
    </div>
  );
}

function CreateStoreInline({
  slug,
  token,
  onCancel,
  onCreated,
}: {
  slug: string;
  token: string | null;
  onCancel: () => void;
  onCreated: (storeID: string) => void;
}) {
  const { t } = useTranslation('common');
  const teams = useQuery({
    queryKey: ['teams', slug, 'all'],
    queryFn: () => listTeams(token, slug, { limit: 200 }),
    enabled: !!slug,
  });
  const [teamID, setTeamID] = useState('');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createStore(token, slug, { team_id: teamID, name }),
    onSuccess: (res) => onCreated(res.id),
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h2 className="font-heading text-lg font-bold mb-3">{t('shop.sales.newSale')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.sales.team')}</span>
          <Select value={teamID} onValueChange={setTeamID}>
            <SelectTrigger>
              <SelectValue placeholder={t('shop.sales.teamPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {(teams.data?.teams ?? []).map((tm) => (
                <SelectItem key={tm.id} value={tm.id}>
                  {tm.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.sales.saleName')}</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('shop.sales.saleNamePlaceholder')}
          />
        </label>
      </div>
      {create.error instanceof Error && (
        <p className="mt-2 text-sm text-destructive">{create.error.message}</p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={create.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={!teamID || !name.trim() || create.isPending}
        >
          {create.isPending ? t('common.loading') : t('common.create')}
        </Button>
      </div>
    </div>
  );
}

const PAYOUT_SORT_COLUMNS = ['team', 'requestedAt', 'amount', 'status'] as const;
type PayoutSortColumn = (typeof PAYOUT_SORT_COLUMNS)[number];

function PayoutsTab({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [filter, setFilter] = useState<'requested' | 'all'>('requested');
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'payouts', slug, filter],
    queryFn: () => listPayouts(token, slug, filter === 'all' ? '' : 'requested'),
    enabled: !!slug,
  });

  const approve = useMutation({
    mutationFn: (id: string) => approvePayout(token, slug, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop', 'payouts', slug] }),
  });

  const sortState = useTableSortState<PayoutSortColumn>({
    columns: PAYOUT_SORT_COLUMNS,
    prefix: 'payouts',
  });
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'payouts',
    defaultSize: 25,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const pending = useMemo(() => rows.filter((p) => p.status === 'requested'), [rows]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    // Default sort: newest-requested first (matches the previous list order).
    const col = sortState.sort ?? 'requestedAt';
    const defaultDir = sortState.sort ? sortState.dir : 'desc';
    const factor = defaultDir === 'desc' ? -1 : 1;
    arr.sort((a: PayoutSummary, b: PayoutSummary) => {
      switch (col) {
        case 'team':
          return factor * a.team_name.localeCompare(b.team_name);
        case 'amount':
          return factor * (a.amount_minor - b.amount_minor);
        case 'status':
          return factor * a.status.localeCompare(b.status);
        case 'requestedAt':
        default:
          return (
            factor *
            (new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime())
          );
      }
    });
    return arr;
  }, [rows, sortState.sort, sortState.dir]);

  const total = sorted.length;
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * size, page * size),
    [sorted, page, size],
  );

  if (error) return <ErrorBlock error={error} />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filter} onValueChange={(v) => setFilter(v as 'requested' | 'all')}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="requested">{t('shop.payouts.filterPending')}</SelectItem>
            <SelectItem value="all">{t('shop.payouts.filterAll')}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {t('shop.payouts.pendingCount', { count: pending.length })}
        </p>
      </div>
      {isLoading ? (
        <TableSkeleton columns={5} />
      ) : total === 0 ? (
        <EmptyState
          icon={<Receipt weight="duotone" />}
          title={t('shop.payouts.emptyTitle')}
          body={t('shop.payouts.emptyBody')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeaderButton
                      column="team"
                      label={t('shop.payouts.col.team')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[12rem]">
                    <SortableHeaderButton
                      column="requestedAt"
                      label={t('shop.payouts.col.requestedAt')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>{t('shop.payouts.col.note')}</TableHead>
                  <TableHead className="w-[10rem] text-right">
                    <SortableHeaderButton
                      column="amount"
                      label={t('shop.payouts.col.amount')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[8rem]">
                    <SortableHeaderButton
                      column="status"
                      label={t('shop.payouts.col.status')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[8rem] text-right">
                    <span className="sr-only">{t('shop.payouts.approveCta')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((p: PayoutSummary) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Receipt className="h-5 w-5 text-foreground/60" weight="bold" />
                        <span className="font-heading font-semibold truncate">{p.team_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(p.requested_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate">
                      {p.note}
                    </TableCell>
                    <TableCell className="text-right font-heading font-semibold tabular-nums">
                      {formatMoney(p.amount_minor, p.currency)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">
                      {p.status}
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === 'requested' && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const ok = await confirm({
                              title: t('shop.payouts.approveTitle'),
                              body: t('shop.payouts.approveBody', {
                                amount: formatMoney(p.amount_minor, p.currency),
                                team: p.team_name,
                              }),
                              confirmLabel: t('shop.payouts.approveCta'),
                            });
                            if (ok) approve.mutate(p.id);
                          }}
                          disabled={approve.isPending}
                        >
                          <CheckCircle className="h-4 w-4" weight="bold" />
                          {t('shop.payouts.approveCta')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            total={total}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
        </div>
      )}
    </div>
  );
}

// ErrorBlock is the catch-all for unexpected server errors on this hub. 403
// (e.g. a leader using the payouts tab) gets a friendlier message because it
// happens by design — see the org-admin gate on ListPayouts.
function ErrorBlock({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.status === 403) {
    return <AccessDenied />;
  }
  return <LoadError />;
}
